import React, { useState, useEffect } from 'react';
import { Box, Alert } from '@mui/material';
import TransactionBuilder from '../../transaction-builder/TransactionBuilder';
import StepContainer from './StepContainer';
import StepActions from './StepActions';
import logger from '../../../utils/logger';

interface FunctionBuilderStepProps {
    step: any;
    inputs?: { [key: string]: string };
    onComplete: (outputs: any) => void;
    onCancel: () => void;
    workflowContext: any;
}

const FunctionBuilderStep: React.FC<FunctionBuilderStepProps> = ({
    step,
    inputs = {},
    onComplete,
    onCancel,
    workflowContext
}) => {
    const [capturedFunctionData, setCapturedFunctionData] = useState<string | null>(null);
    const [capturedTargetContract, setCapturedTargetContract] = useState<string | null>(null);
    const [capturedDescription, setCapturedDescription] = useState<string | null>(null);
    const [hasData, setHasData] = useState(false);

    // Listen for function built events from the embedded TransactionBuilder
    useEffect(() => {
        const handleFunctionBuilt = (event: CustomEvent) => {
            const { functionReference, secureReference, targetContract, functionName } = event.detail;

            logger.log('🎯 FunctionBuilderStep: Captured function data from TransactionBuilder:', {
                functionReference,
                secureReference,
                targetContract,
                functionName,
                isSecureReference: secureReference && secureReference.startsWith('{{') && secureReference.endsWith('}}')
            });

            // Update captured data
            setCapturedFunctionData(secureReference);
            setCapturedTargetContract(targetContract);
            setCapturedDescription(`${functionName} function call`);
            setHasData(true);
        };

        // Listen for the functionBuilt event from FunctionBuilder
        const eventListener = (event: Event) => {
            handleFunctionBuilt(event as CustomEvent);
        };

        window.addEventListener('functionBuilt', eventListener);

        return () => {
            window.removeEventListener('functionBuilt', eventListener);
        };
    }, []);

    const handleComplete = () => {
        if (capturedFunctionData && capturedTargetContract) {
            logger.log('🎯 FunctionBuilderStep: Completing step with captured data:', {
                functionData: capturedFunctionData.startsWith('{{') ? capturedFunctionData : capturedFunctionData.substring(0, 20) + '...',
                targetContract: capturedTargetContract,
                isSecureReference: capturedFunctionData.startsWith('{{') && capturedFunctionData.endsWith('}}')
            });

            onComplete({
                functionData: capturedFunctionData,
                targetContract: capturedTargetContract,
                description: capturedDescription || 'Function call created'
            });
        } else {
            logger.warn('⚠️ FunctionBuilderStep: No function data captured yet');
        }
    };

    return (
        <StepContainer>
            <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Step 1: Build Memer Function Call</strong>
                <br />
                Use the Transaction Builder below to create your function call. Once you've built and encoded your function, click "Capture Function Data" to continue to the next step.
            </Alert>

            {/* ✅ PRESERVE: Embedded utility pattern for single-screen UX */}
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                <TransactionBuilder workflowContext={workflowContext} />
            </Box>

            <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Instructions:</strong>
                <br />
                1. Select your target contract from the dropdown
                <br />
                2. Choose the function you want to call
                <br />
                3. Fill in the function parameters
                <br />
                4. Click "Build Function" to generate the secure reference
                <br />
                5. Click "Capture Function Data" below when you see the green confirmation
            </Alert>

            <StepActions
                onComplete={handleComplete}
                onCancel={onCancel}
                completeLabel={hasData ? 'Capture Function Data & Continue' : 'Build Function First'}
                completeDisabled={!hasData}
                completeColor="primary"
            />
        </StepContainer>
    );
};

export default FunctionBuilderStep;
